import { css } from '@emotion/react';
import type { SvgIconComponent } from '@mui/icons-material';
import { type ComponentType, type SVGProps, useEffect, useRef } from 'react';

import { formatUsd } from '@kaiizen/kai-presenters';

import { LockIcon } from '@/Client/Icons';
import {
  type ClassifiedEntry,
  classifyEntry,
  collapseConsecutiveSameKind,
  combineProjectReassignments,
  formatRelativeTime,
  getAboutUserLabel,
  getActivityLogMetadataNumber,
  getActorLabel,
  type TimelineEventKey,
  useScanNavLink,
} from '@/Client/Lib';
import { useProjectActivityLog } from '@/Client/State';
import { KAI_SHIKI_LAYOUT, styleguideColors, styleguideFontSize, styleguideFontWeight } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { Button, LabeledCard, LabelValueRow } from '@/Client/Widgets';
import { styleguideFontFamily, styleguideLetterSpacing } from '@kaiizen/kai-shiki/core';

const TIMELINE_STYLE = css`
  label: TIMELINE_STYLE;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  margin-top: -${KAI_SHIKI_LAYOUT.spacing1}px;
`;

const EMPTY_STATE_STYLE = css`
  label: EMPTY_STATE_STYLE;
  font-family: ${styleguideFontFamily.body};
  font-size: ${pxToRem(styleguideFontSize.bodyDefault)};
  color: ${styleguideColors.textSecondary};
  display: flex;
  min-height: 220px;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const ENTRY_LIST_STYLE = css`
  label: ENTRY_LIST_STYLE;
  display: flex;
  flex-direction: column;
`;

const ENTRY_STYLE = css`
  label: ENTRY_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${KAI_SHIKI_LAYOUT.spacing1}px;
  padding: ${KAI_SHIKI_LAYOUT.spacing2}px 0;
  border-bottom: 1px solid ${styleguideColors.stroke};

  &:last-child {
    border-bottom: 0;
  }
`;

const ENTRY_STYLE_NO_ICON = css`
  label: ENTRY_STYLE_NO_ICON;
`;

const ICON_WRAPPER_STYLE = css`
  label: ICON_WRAPPER_STYLE;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ENTRY_HEADER_STYLE = css`
  label: ENTRY_HEADER_STYLE;
  display: flex;
  gap: ${KAI_SHIKI_LAYOUT.spacing1}px;
  align-items: center;
`;

const ENTRY_TITLE_STYLE = css`
  label: ENTRY_TITLE_STYLE;
  font-family: ${styleguideFontFamily.display};
  font-size: ${pxToRem(styleguideFontSize.displaySmall)};
  font-weight: ${styleguideFontWeight.bold};
  letter-spacing: ${styleguideLetterSpacing.monoLabel};
  color: ${styleguideColors.ink};
  line-height: 1.15;
`;

const ENTRY_TIME_STYLE = css`
  label: ENTRY_TIME_STYLE;
  font-family: ${styleguideFontFamily.body};
  font-size: ${pxToRem(styleguideFontSize.bodyDefault)};
  color: ${styleguideColors.textTertiary};
  white-space: nowrap;
`;

const ENTRY_META_STYLE = css`
  label: ENTRY_META_STYLE;
  font-family: ${styleguideFontFamily.body};
  font-size: ${pxToRem(styleguideFontSize.bodyDefault)};
  color: ${styleguideColors.textTertiary};
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: ${KAI_SHIKI_LAYOUT.spacing1}px;
  margin-top: 0;
  min-width: 0;
`;

const ENTRY_ACTOR_STYLE = css`
  label: ENTRY_ACTOR_STYLE;
  color: ${styleguideColors.textSecondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const DATA_ROWS_STYLE = css`
  label: DATA_ROWS_STYLE;
  font-family: ${styleguideFontFamily.body};
  font-size: ${pxToRem(styleguideFontSize.bodyDefault)};
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: ${KAI_SHIKI_LAYOUT.spacing1}px;
`;

const NAV_LINK_CONTAINER_STYLE = css`
  label: NAV_LINK_CONTAINER_STYLE;
  display: flex;
  justify-content: flex-end;
  margin-top: ${KAI_SHIKI_LAYOUT.spacing1p5}px;
`;

const NAV_LINK_STYLE = css`
  label: NAV_LINK_STYLE;
  font-family: ${styleguideFontFamily.display};
  font-size: ${pxToRem(styleguideFontSize.bodyDefault)};
  font-weight: ${styleguideFontWeight.bold};
  letter-spacing: ${styleguideLetterSpacing.monoLabel};
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${styleguideColors.ink};
  background: ${styleguideColors.accent};
  border: none;
  padding: 4px 10px;
  cursor: pointer;

  &:hover {
    background: ${styleguideColors.accentDeep};
  }
`;

const LOAD_MORE_STYLE = css`
  label: LOAD_MORE_STYLE;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: ${KAI_SHIKI_LAYOUT.spacing1p5}px;
`;

const LOAD_MORE_SENTINEL_STYLE = css`
  label: LOAD_MORE_SENTINEL_STYLE;
  width: 100%;
  height: 1px;
`;

const LOAD_MORE_TEXT_STYLE = css`
  label: LOAD_MORE_TEXT_STYLE;
  font-family: ${styleguideFontFamily.body};
  font-size: ${pxToRem(styleguideFontSize.bodyDefault)};
  color: ${styleguideColors.textSecondary};
`;

const ScopeIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg width='15' height='19' viewBox='0 0 15 19' fill='none' xmlns='http://www.w3.org/2000/svg' {...props}>
    <rect x='4.29175' y='0.5' width='5.58346' height='2.46154' stroke='currentColor' />
    <path d='M10.046 1.38461H13.6669V18H0.5V1.38461H4.1209' stroke='currentColor' />
  </svg>
);

const ChangeOrderIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg width='20' height='16' viewBox='0 0 20 16' fill='none' xmlns='http://www.w3.org/2000/svg' {...props}>
    <path d='M19.0986 15.5H0.901367L10 0.942383L19.0986 15.5Z' stroke='currentColor' />
  </svg>
);

const PunchListIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg width='21' height='21' viewBox='0 0 21 21' fill='none' xmlns='http://www.w3.org/2000/svg' {...props}>
    <circle cx='10.5' cy='10.5' r='10' stroke='currentColor' />
    <path d='M6.21387 10.5518L13.3567 14.7863V10.5006V6.21484L6.21387 10.5518Z' stroke='currentColor' />
  </svg>
);

const CloseoutIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg' {...props}>
    <rect x='0.707107' y='10' width='13.1421' height='13.1421' transform='rotate(-45 0.707107 10)' stroke='currentColor' />
    <path d='M7.07104 10.1358L9.16618 12.4928L13.3564 7.77878' stroke='currentColor' />
  </svg>
);

type EventVisualConfig = {
  color: string;
  Icon?: SvgIconComponent | ComponentType<SVGProps<SVGSVGElement>>;
  status?: string;
};

const EVENT_VISUALS: Record<TimelineEventKey, EventVisualConfig> = {
  projectCreatedOrRestarted: { color: styleguideColors.ink, status: 'Created' },
  projectHandedOff: { color: styleguideColors.ink, status: 'Handed off' },
  projectWorkTaken: { color: styleguideColors.ink, status: 'Work taken' },
  projectReassigned: { color: styleguideColors.ink, status: 'Reassigned' },
  initialWalkAssigned: { color: styleguideColors.ink, status: 'Assigned' },
  walkAssignedDispatchedReassigned: { color: styleguideColors.ink, status: 'Assigned' },
  fieldUserAdded: { color: styleguideColors.ink, status: 'Added' },
  fieldUserRemoved: { color: styleguideColors.ink, status: 'Removed' },
  initialWalkCompleted: { color: styleguideColors.ink, Icon: ScopeIcon, status: 'Submitted' },
  scopeEditRequested: { color: styleguideColors.ink, Icon: ScopeIcon, status: 'Requested' },
  scopeApproved: { color: styleguideColors.ink, Icon: ScopeIcon, status: 'Approved' },
  progressWalkSubmitted: { color: styleguideColors.midtone, status: 'Submitted' },
  changeOrderCreated: { color: styleguideColors.midtone, Icon: ChangeOrderIcon, status: 'Created' },
  changeOrderSubmitted: { color: styleguideColors.midtone, Icon: ChangeOrderIcon, status: 'Submitted' },
  changeOrderApprovedRejected: { color: styleguideColors.midtone, Icon: ChangeOrderIcon, status: 'Reviewed' },
  punchlistCreated: { color: styleguideColors.midtone, Icon: PunchListIcon, status: 'Created' },
  punchlistTaskUpdated: { color: styleguideColors.midtone, Icon: PunchListIcon, status: 'Updated' },
  closeoutCreated: { color: styleguideColors.success, Icon: CloseoutIcon, status: 'Created' },
  closeoutSubmitted: { color: styleguideColors.success, Icon: CloseoutIcon, status: 'Submitted' },
  closeoutApproved: { color: styleguideColors.success, Icon: CloseoutIcon, status: 'Approved' },
  managerAdded: { color: styleguideColors.ink, status: 'Added' },
  managerRemoved: { color: styleguideColors.ink, status: 'Removed' },
};

const isLockedStatus = (status?: string) => status === 'pendingReview' || status === 'inReview' || status === 'approved';

const AmountRow = ({ label, totalAmount, previousTotalAmount }: {
  label: string;
  totalAmount?: number;
  previousTotalAmount?: number;
}) => {
  const showPrevious = previousTotalAmount != null && previousTotalAmount !== totalAmount;
  if (totalAmount == null && !showPrevious) return null;
  return (
    <LabelValueRow
      label={label}
      value={formatUsd(totalAmount)}
      previousValue={showPrevious ? formatUsd(previousTotalAmount) : undefined}
      tone='positive'
    />
  );
};

const ActivityLogEntry = ({ classified }: { classified: ClassifiedEntry }) => {
  const { entry, emphasis, suffix, key } = classified;
  const visual = EVENT_VISUALS[key];
  const Icon = visual.Icon;
  const color = visual.color;
  const getNavLink = useScanNavLink();
  const navLink = getNavLink(key, entry.scanId);
  const locked = isLockedStatus(entry.scanLifecycleStatus);
  const aboutUserLabel = entry.aboutUserName ? getAboutUserLabel(key) : undefined;
  const reassignmentDetails = classified.reassignmentDetails;
  const approvedScopeBudget = getActivityLogMetadataNumber(entry, 'approvedScopeBudget');

  return (
    <div css={[ENTRY_STYLE, !Icon && ENTRY_STYLE_NO_ICON]}>
      <div css={ENTRY_HEADER_STYLE}>
        {Icon
          ? (
            <div css={ICON_WRAPPER_STYLE}>
              <Icon sx={{ color, fontSize: pxToRem(20) }} />
            </div>
          )
          : null}
        <div css={ENTRY_TITLE_STYLE}>
          <span style={{ color }}>{emphasis}</span> {suffix}
        </div>
      </div>
      <div>
        <div css={ENTRY_META_STYLE}>
          <span css={ENTRY_ACTOR_STYLE}>{getActorLabel(entry)}</span>
          <span css={ENTRY_TIME_STYLE}>{formatRelativeTime(entry.createdAt)}</span>
        </div>

        <div css={DATA_ROWS_STYLE}>
          {key === 'projectReassigned'
            ? (
              <>
                {reassignmentDetails?.removedUsers.length ? <LabelValueRow label='Removed' value={reassignmentDetails.removedUsers.join(', ')} /> : null}
                {reassignmentDetails?.addedUsers.length ? <LabelValueRow label='Added' value={reassignmentDetails.addedUsers.join(', ')} /> : null}
              </>
            )
            : aboutUserLabel && entry.aboutUserName
            ? <LabelValueRow label={aboutUserLabel} value={entry.aboutUserName} />
            : null}

          {key === 'closeoutApproved'
            ? (
              <>
                <AmountRow label='Approved amount' totalAmount={entry.totalAmount} />
                {entry.previousTotalAmount != null
                  ? <LabelValueRow label='Approved change orders' value={formatUsd(entry.previousTotalAmount)} tone='positive' />
                  : null}
                {approvedScopeBudget != null
                  ? <LabelValueRow label='Approved scope budget' value={formatUsd(approvedScopeBudget)} tone='positive' />
                  : null}
              </>
            )
            : (key === 'scopeApproved' || key === 'changeOrderApprovedRejected')
            ? (
              <AmountRow
                label={key === 'scopeApproved' || entry.eventType === 'changeOrderApproved' ? 'Approved amount' : 'Total amount'}
                totalAmount={entry.totalAmount}
                previousTotalAmount={entry.previousTotalAmount}
              />
            )
            : (entry.totalAmount != null && entry.eventType !== 'walkCreated' && (
                key === 'initialWalkCompleted'
                || key === 'scopeEditRequested'
                || key === 'changeOrderSubmitted'
                || key === 'closeoutSubmitted'
              ))
            ? (
              <AmountRow
                label={entry.eventType === 'scopeSubmitted' || entry.eventType === 'scopeRejected' || key === 'changeOrderSubmitted'
                  ? 'Pending amount'
                  : 'Total amount'}
                totalAmount={entry.totalAmount}
                previousTotalAmount={entry.previousTotalAmount}
              />
            )
            : null}
        </div>

        {navLink
          ? (
            <div css={NAV_LINK_CONTAINER_STYLE}>
              <button css={NAV_LINK_STYLE} onClick={navLink.onClick}>
                {locked ? <LockIcon size={10} /> : null}
                {navLink.label} →
              </button>
            </div>
          )
          : null}
      </div>
    </div>
  );
};

export const ProjectTimeline = ({ projectId, title = 'Activities' }: { projectId?: string | null; title?: string }) => {
  const { entries, isLoading, hasMore, loadMore, isLoadingMore } = useProjectActivityLog(projectId, { limit: 20 });
  const classifiedEntries = entries.map(classifyEntry).filter((entry): entry is ClassifiedEntry => !!entry);
  const groupedEntries = combineProjectReassignments(classifiedEntries);
  const collapsedEntries = collapseConsecutiveSameKind(groupedEntries);
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
  const hasIntersectionObserver = typeof IntersectionObserver !== 'undefined';

  useEffect(() => {
    if (!hasIntersectionObserver || !hasMore || isLoadingMore || !loadMoreSentinelRef.current) return;
    const observer = new IntersectionObserver(entriesObserved => {
      if (!entriesObserved.some(entry => entry.isIntersecting)) return;
      void loadMore();
    }, { rootMargin: '200px 0px' });
    observer.observe(loadMoreSentinelRef.current);
    return () => observer.disconnect();
  }, [classifiedEntries.length, hasIntersectionObserver, hasMore, isLoadingMore, loadMore]);

  return (
    <LabeledCard title={title}>
      <div css={TIMELINE_STYLE}>
        {!isLoading && collapsedEntries.length === 0 ? <div css={EMPTY_STATE_STYLE}>No activity yet for this project.</div> : (
          <div css={ENTRY_LIST_STYLE}>
            {collapsedEntries.map(classified => <ActivityLogEntry key={classified.entry.id} classified={classified} />)}
          </div>
        )}
        {hasMore && (
          <div css={LOAD_MORE_STYLE}>
            {hasIntersectionObserver
              ? (
                <>
                  <div ref={loadMoreSentinelRef} css={LOAD_MORE_SENTINEL_STYLE} />
                  {isLoadingMore ? <span css={LOAD_MORE_TEXT_STYLE}>Loading...</span> : null}
                </>
              )
              : (
                <Button buttonStyle='gray' onClick={() => loadMore()} disabled={isLoadingMore}>
                  {isLoadingMore ? 'Loading...' : 'Load More'}
                </Button>
              )}
          </div>
        )}
      </div>
    </LabeledCard>
  );
};
